/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PropsWithChildren } from 'react';
import { Spacing, SpacingName, Typography } from '@doveit/lego';
import { NodeRendererType, RichText, RichTextProps } from '@graphcms/rich-text-react-renderer';
import * as styles from './EditorialContentPage.style';
import Markdown from '../markdown/Markdown';
import PageLayout from '../../layouts/page-layout/PageLayout';
import { ComponentKey, WidgetComponentsUnion } from './types';
import { widgetComponentsMapper } from './cmsComponentsMapper';
import { renderers } from './richTextRenderers';
import PageHead, { PageHeadProps } from '../page-head/PageHead';
import AppLayout from '../../layouts/app-layout/AppLayout';
import LandingLayout from '../../layouts/landing-layout/LandingLayout';
import { ContactUsSectionComponent, TrustpilotSectionComponent } from '../../../domain/graphcms';

export interface ComponentBlock {
  id: string,
  component: React.ComponentType,
  componentProps: any,
}

export interface EditorialContentPageProps {
  isLanding?: boolean,
  SEO: PageHeadProps['SEO'],
  title: string,
  renderersOverrides?: NodeRendererType,
  mainContent: RichTextProps['content'],
  mainContentReferences: RichTextProps['references'],
  secondaryBlocks?: WidgetComponentsUnion[],
  extraMainContent?: (ContactUsSectionComponent | TrustpilotSectionComponent)[],
}

export const EditorialContentPage: React.FC<PropsWithChildren<EditorialContentPageProps>> = ({
  isLanding = false,
  SEO,
  title,
  renderersOverrides,
  mainContent,
  mainContentReferences,
  secondaryBlocks = [],
  extraMainContent = [],
  children,
}) => {
  const secondaryBlocksData = React.useMemo(() => (secondaryBlocks).map((componentData) => {
    const key = componentData.__typename;

    if (!(key in widgetComponentsMapper)) {
      throw new Error(`Unknown component type: ${key}`);
    }

    const mapper = widgetComponentsMapper[key as ComponentKey];

    return {
      id: componentData.id,
      name: mapper.name,
      component: mapper.component,
      componentProps: mapper.toProps(componentData),
    };
  }), [secondaryBlocks]);

  const extraMainContentBlocksData = React.useMemo(() => (extraMainContent).map((componentData) => {
    const key = componentData.__typename;

    if (!(key in widgetComponentsMapper)) {
      throw new Error(`Unknown component type: ${key}`);
    }

    const mapper = widgetComponentsMapper[key as ComponentKey];

    return {
      id: componentData.id,
      name: mapper.name,
      component: mapper.component,
      componentProps: mapper.toProps({
        ...componentData,
        isLanding,
      }),
    };
  }), [extraMainContent, isLanding]);

  const Layout = isLanding ? LandingLayout : AppLayout;

  return (
    <Layout>
      <PageHead
        SEO={SEO}
        type="article"
      />
      <Spacing margin={[SpacingName.BIG, 0]}>
        <PageLayout>
          <PageLayout.Wrapper>
            <PageLayout.Header css={styles.header}>
              <Typography.H2 tagName="h1">
                <Markdown>
                  {title}
                </Markdown>
              </Typography.H2>
            </PageLayout.Header>
            <PageLayout.Content>
              <PageLayout.Main>
                <article css={styles.mainContent}>
                  <RichText
                    content={mainContent}
                    renderers={renderers(renderersOverrides)}
                    references={mainContentReferences}
                  />
                </article>
                {extraMainContentBlocksData.map(({
                  id, name, component: Component, componentProps,
                }) => (
                  <div key={id} className={name}>
                    <Component
                      {...(componentProps as any)}
                      padding={[SpacingName.BIG, 0, 0]}
                    />
                  </div>
                ))}
                {children}
              </PageLayout.Main>
              {secondaryBlocksData.length > 0 && (
                <PageLayout.Aside>
                  <div css={styles.secondaryBlocks}>
                    {secondaryBlocksData.map(({
                      id, name, component: Component, componentProps,
                    }) => (
                      <div key={id} className={name}>
                        <Component {...(componentProps as any)} />
                      </div>
                    ))}
                  </div>
                </PageLayout.Aside>
              )}
            </PageLayout.Content>
          </PageLayout.Wrapper>
        </PageLayout>
      </Spacing>
    </Layout>
  );
};

export default EditorialContentPage;
